import * as React from 'react';
import { useEffect, useState, useRef } from "react";
import ReactPaginate from "react-paginate";
import client from "../../../api/client"; // Adjust the import path if needed
import {
  getAdminInitData,
  getAdminComments, 
} from "../../../api/sdk.gen"; // Adjust the import path if needed
import { Comment } from "../../../api/types.gen";
import Cookies from "js-cookie";
import Select from 'react-select';

import { formatDate } from '../../../helpers/dates'
import { truncateWithTooltip } from '../../../helpers/utils'

const AdminComments = () => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  
  const [sortColumn, setSortColumn] = useState<string>(
    Cookies.get("sortColumn") || "id"
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    (Cookies.get("sortDirection") as "asc" | "desc") || "asc"
  );

  const params = new URLSearchParams(location.search);
  const initialFilters: { [key: string]: string | string[] } = {};
  params.forEach((value, key) => {
    if (key.includes("[]")) {
      const cleanKey = key.replace("[]", "");
      if (!initialFilters[cleanKey]) {
        initialFilters[cleanKey] = [];
      }
      (initialFilters[cleanKey] as string[]).push(value);
    } else {
      initialFilters[key] = value;
    }
  });

  const [page, setPage] = useState<number>(parseInt(params.get('page')) || 1);
  const [per, setPer] = useState<number>(parseInt(params.get('per')) || 10);
  const [filters, setFilters] = useState<{ [key: string]: string | string[] }>(initialFilters);
  
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const tableContainerRef = useRef(null);
  const [tableMaxHeight, setTableMaxHeight] = useState("auto");

  client;

  useEffect(() => {
    const updateTableHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      const footerHeight = footerRef.current?.offsetHeight || 0;
      const viewportHeight = window.innerHeight;

      const maxHeight = Math.max(500, viewportHeight - headerHeight - footerHeight - 100);
      setTableMaxHeight(`${maxHeight}px`);
    };

    // Set height on mount and resize
    setTimeout(() => {
      updateTableHeight();
    }, 1000)

    window.addEventListener("resize", updateTableHeight);

    return () => {
      window.removeEventListener("resize", updateTableHeight);
    };
  }, []);

  // Update URL params when filters change
  useEffect(() => {
    const newParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val) => newParams.append(`${key}[]`, val));
      } else {
        newParams.set(key, value);
      }
    });

    if (page) newParams.set('page', page.toString());
    if (per) newParams.set('per', per.toString());

    // Update the browser's URL without reloading the page
    const newUrl = `${window.location.pathname}?${newParams.toString()}`;
    window.history.pushState(null, "", newUrl);
  }, [page, per, filters]);

 
  const fetchComments = async () => {
    setLoading(true);
    try {
      const params: any = {
        page,
        per,
      };

      if (sortColumn && sortDirection) {
        params.sort_col = sortColumn;
        params.sort_dir = sortDirection;
      }

      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          params[`filters[${key}][]`] = value;
        } else {
          params[`filters[${key}]`] = value;
        }
      });

      const response = await getAdminComments({
        query: params,
      });

      setComments(response.data.data);
      setRowCount(response.data.meta?.count || 0);
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchComments();
  }, [page, per, sortColumn, sortDirection, filters]);

  useEffect(() => {
    Cookies.set("sortColumn", sortColumn || "");
    Cookies.set("sortDirection", sortDirection || "");
  }, [sortColumn, sortDirection]);

  const formatLabel = (key: string) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase())
  }

  const handleFilterChange = (column: string, value: string) => {
    const updatedFilters = {
      ...filters,
      [column]: value,
    };
    setFilters(updatedFilters);
    setPage(1);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const columns = [
    {
      key: "id",
      name: "ID",
      sortable: true,
      filterType: "text",
    },
    {
      key: "title",
      name: "Title",
      sortable: true,
      filterType: "text",
    },
    {
      key: "subject",
      name: "Subject",
      sortable: true,
      filterType: "text",
    },
    {
      key: "body",
      name: "Body",
      sortable: true,
      filterType: "text",
    },
    {
      key: "user_id",
      name: "User ID",
      sortable: true,
      filterType: "text",
    },
    {
      key: "user_name",
      name: "User Name",
      sortable: false,
      filterType: "none",
    },
    {
      key: "commentable_id",
      name: "Commentable ID",
      sortable: false,
      filterType: "text",
    },
    {
      key: "commentable_type",
      name: "Commentable Type",
      sortable: false,
      filterType: "text",
    },
    {
      key: "created_at",
      name: "DATE CREATED",
      sortable: true,
      filterType: "none",
    },
    {
      key: "updated_at",
      name: "DATE MODIFIED",
      sortable: true,
      filterType: "none",
    },
  ];

  const formatCellValue = (value) => {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "-";
    }
    if (value == null || value === "") return "-";

    const stringValue = String(value);
    return stringValue.length > 200 ? truncateWithTooltip(stringValue) : stringValue;
  };

  const resolveNestedValue = (obj, key) =>
    key.split(".").reduce((acc, field) => (acc && acc[field] ? acc[field] : null), obj);

  const getSpecialCellValue = (item, key) => {
    if (key === "updated_at" || key === "created_at") return formatDate(item[key]);

    return null;
  };

  // Main function for getting cell value
  const getCellValue = (item, key) => {
    const specialValue = getSpecialCellValue(item, key);
    if (specialValue !== null) return specialValue;

    const value = resolveNestedValue(item, key);
    return formatCellValue(value);
  };

  // Render cell content
  const renderCellContent = (item, col) => {
    const originalValue = getCellValue(item, col.key);

    if (col.renderer) {
      return col.renderer(item, originalValue)
    }

    return <div dangerouslySetInnerHTML={{ __html: originalValue }} />;
  };

  return (
    <div className="page-wrap">
      <div className="header-wrap" ref={headerRef}>
        <div className="header-1" style={{display: 'flex', justifyContent: 'space-between', alignItems:'center'}}>
          <h1>Comments</h1>
        </div>
      </div>
      <div className="table-container" style={{ maxHeight: tableMaxHeight }} ref={tableContainerRef}>
        <table className="table-with-fixed-columns">
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th
                  key={col.key}
                >
                  <div className="header-content">
                    <div>
                      <span>{col.name}</span>
                      {col.sortable && (
                        <span
                          className="sort-icon"
                          onClick={() => handleSort(col.key)}
                          style={{cursor:'pointer'}}
                        >
                          {sortColumn === col.key ? (
                            sortDirection === "asc" ? (
                              " 🔼"
                            ) : (
                              " 🔽"
                            )
                          ) : (
                            " ↕️"
                          )}
                        </span>
                      )}
                    </div>
                    <div>
                      {col.filterType === "text" && (
                        <input
                          type="text"
                          placeholder="Filter"
                          defaultValue={filters[col.key] || ""}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleFilterChange(col.key, (e.target as HTMLInputElement).value);
                            }
                          }}
                          onBlur={(e) => handleFilterChange(col.key, (e.target as HTMLInputElement).value)}
                        />
                      )}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comments.map((lo) => (
              <tr 
                key={lo.id} 
              >
                {columns.map((col, index) => (
                  <td
                    key={col.key}
                  >
                    {renderCellContent(lo, col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pagination-wrap" ref={footerRef}>
        <div className="pagination-per">
          Displaying 
          <select
            value={per}
            onChange={(e) => {
              setPer(parseInt(e.target.value));
              setPage(1);
            }}
          >
            {[10, 20, 50, 100, 500].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          items of {rowCount}
        </div>
        <ReactPaginate
          previousLabel={"Prev"}
          nextLabel={"Next"}
          breakLabel={"..."}
          pageCount={Math.ceil(rowCount / per)}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={(selected) => setPage(selected.selected + 1)}
          containerClassName={"pagination"}
          activeClassName={"active"}
        />
      </div>
    </div>
  );
};

export default AdminComments;
